/* =============================================================
   Sidebar.jsx — Admin portal navigation sidebar
   -------------------------------------------------------------
   Fixed left sidebar for all admin screens. Expands to 280px
   or collapses to 72px (icon-only) via SidebarContext toggle.

   Shows a pending-document badge on "Incoming Students" when
   there are documents awaiting admin review.

   Sections:
   - Logo + collapse/expand toggle
   - Main navigation links
   - Settings, user info, and sign-out at the bottom
   ============================================================= */

import { useNavigate, useLocation } from 'react-router-dom';
import { useSidebar }   from '../context/SidebarContext';
import { useDocuments } from '../context/DocumentContext';
import { useAuth }      from '../context/AuthContext';


/* -------------------------------------------------------------
   NAVIGATION ITEMS
   ------------------------------------------------------------- */
const mainNav = [
  { label: 'Dashboard',          icon: 'dashboard',      path: '/dashboard'    },
  { label: 'Incoming Students',  icon: 'flight_land',    path: '/incoming'     },
  { label: 'Outgoing Students',  icon: 'flight_takeoff', path: '/outgoing'     },
  { label: 'University Partners',icon: 'public',         path: '/partners'     },
  { label: 'Analytics',          icon: 'bar_chart',      path: '/analytics'    },
  { label: 'AI Assistant',       icon: 'smart_toy',      path: '/ai-assistant' },
];


/* -------------------------------------------------------------
   SIDEBAR COMPONENT
   ------------------------------------------------------------- */
export default function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { open, toggle }      = useSidebar();
  const { pendingIncoming, pendingOutgoing } = useDocuments();
  const { user, logout }                    = useAuth();

  // logout() immediately sets user to null and notifies React.
  // ProtectedRoute sees user=null and redirects to /login automatically —
  // no navigate() or page reload needed. The Supabase listener stays alive.
  const handleLogout = async () => {
    await logout();
  };

  const isActive = (path) => location.pathname.startsWith(path);

  return (
    <div
      className="fixed left-0 top-0 h-full bg-[#0F172A] flex flex-col z-40 transition-all duration-300"
      style={{ width: open ? '280px' : '72px' }}
    >
      {/* Logo */}
      <div className="px-3 py-4 flex items-center border-b border-slate-800" style={{ minHeight: '64px' }}>
        <div className="w-11 h-11 rounded-xl overflow-hidden flex-shrink-0 bg-white shadow-sm">
          <img src="/favicon.png" alt="Unixplore" className="w-full h-full object-contain" />
        </div>
        {open && (
          <div className="ml-3 flex-1 min-w-0 overflow-hidden">
            <p className="text-white font-bold font-display text-base leading-tight whitespace-nowrap">Unixplore</p>
            <p className="text-slate-400 text-xs leading-tight mt-0.5 whitespace-nowrap">{user?.university ?? 'University'}</p>
          </div>
        )}
      </div>

      {/* Floating toggle — white circle on the right edge, always visible */}
      <button
        onClick={toggle}
        title={open ? 'Collapse sidebar' : 'Expand sidebar'}
        className="absolute -right-3 top-[22px] w-6 h-6 rounded-full bg-white border border-slate-200 shadow-md flex items-center justify-center text-slate-500 hover:text-indigo-600 hover:shadow-lg transition-all z-50"
      >
        <span className="material-symbols-outlined text-[14px]">
          {open ? 'chevron_left' : 'chevron_right'}
        </span>
      </button>

      {/* Main navigation links */}
      <nav className="flex-1 px-2 py-4 overflow-y-auto scrollbar-hide overflow-x-hidden">
        {open && (
          <p className="text-slate-500 text-xs font-semibold uppercase tracking-wider px-2 mb-2 whitespace-nowrap">
            Main Menu
          </p>
        )}
        <ul className="space-y-1">
          {mainNav.map((item) => (
            <li key={item.path}>
              <button
                onClick={() => navigate(item.path)}
                title={!open ? item.label : undefined}
                className={`w-full flex items-center gap-3 rounded-lg text-sm font-medium transition-colors duration-150 text-left ${
                  open ? 'px-3 py-2.5' : 'justify-center px-0 py-2.5'
                } ${
                  isActive(item.path)
                    ? 'bg-indigo-600 text-white'
                    : 'text-slate-400 hover:bg-slate-800/50 hover:text-slate-200'
                }`}
              >
                <span className="material-symbols-outlined text-[20px] leading-none flex-shrink-0">{item.icon}</span>
                {open && <span className="truncate flex-1">{item.label}</span>}
                {/* Pending documents badge — split by student type */}
                {item.path === '/incoming' && pendingIncoming > 0 && (
                  <span className={`flex-shrink-0 text-[10px] font-bold rounded-full flex items-center justify-center bg-red-500 text-white ${open ? 'w-4 h-4' : 'w-3.5 h-3.5 absolute top-1.5 right-1.5'}`}>
                    {pendingIncoming}
                  </span>
                )}
                {item.path === '/outgoing' && pendingOutgoing > 0 && (
                  <span className={`flex-shrink-0 text-[10px] font-bold rounded-full flex items-center justify-center bg-red-500 text-white ${open ? 'w-4 h-4' : 'w-3.5 h-3.5 absolute top-1.5 right-1.5'}`}>
                    {pendingOutgoing}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      </nav>

      {/* Bottom section: settings, user info, sign out */}
      <div className="px-2 pb-3 border-t border-slate-800 overflow-x-hidden">
        <button
          onClick={() => navigate('/settings')}
          title={!open ? 'Settings' : undefined}
          className={`w-full flex items-center gap-3 rounded-lg text-sm font-medium transition-colors duration-150 text-left mt-3 ${
            open ? 'px-3 py-2.5' : 'justify-center px-0 py-2.5'
          } ${
            isActive('/settings')
              ? 'bg-indigo-600 text-white'
              : 'text-slate-400 hover:bg-slate-800/50 hover:text-slate-200'
          }`}
        >
          <span className="material-symbols-outlined text-[20px] leading-none flex-shrink-0">settings</span>
          {open && <span className="truncate">Settings</span>}
        </button>

        {/* Logged-in user info */}
        <div className={`flex items-center mt-3 gap-3 ${open ? 'px-3 py-2' : 'justify-center py-2'}`}>
          <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0" style={{ backgroundColor: user?.avatarBg ?? '#4343d5' }}>
            {user?.initials ?? 'JK'}
          </div>
          {open && (
            <div className="flex-1 min-w-0">
              <p className="text-slate-200 text-sm font-medium truncate">{user?.name ?? 'Julia Klein'}</p>
              <p className="text-slate-500 text-xs truncate">{user?.title ?? 'IO Manager'}</p>
            </div>
          )}
        </div>

        {/* Sign out */}
        <button
          onClick={handleLogout}
          title={!open ? 'Sign out' : undefined}
          className={`w-full flex items-center gap-3 rounded-lg text-sm font-medium transition-colors mt-0.5 text-slate-400 hover:bg-slate-800/50 hover:text-slate-200 ${open ? 'px-3 py-2' : 'justify-center px-0 py-2.5'}`}
        >
          <span className="material-symbols-outlined text-[20px] leading-none flex-shrink-0">logout</span>
          {open && <span className="truncate">Sign out</span>}
        </button>
      </div>
    </div>
  );
}
